import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Modal, Grid, InputLabel, Button, Box, Typography,
} from '@mui/material';
import API from '../../api/endpoints';
import { sendRequest } from '../../api/shootAPI';
import { NotificationContext } from '../../contexts/Notification';
import { UserContext } from '../../contexts/User';
import { TextInput } from '../../components/muiCustomized/TextInput';
import AvatarUploader from '../../components/avatarUploader';
import DatePicker from '../../components/datePicker';
import PlacesAutocomplete from '../../components/PlacesAutocomplete';

function ChangeDetailsModal({
  formData, address, setFormData, closeModal, profileUpdateRequests, fetchFaceIdData,
}) {
  const { user, setUserAccount } = useContext(UserContext);
  const { displayNotification } = useContext(NotificationContext);
  const [formErrors, setFormErrors] = useState({});

  const [curAddress, setCurAddress] = useState({
    location: { description: address.description },
    latlng: { lat: address.latitude, lng: address.longitude },
  });

  const handleFormData = (e) => {
    if ((e.target.name === 'firstName' || e.target.name === 'lastName') && e.target.value.length > 255) {
      displayNotification({
        message: 'The name must be less than 255 characters.',
        severity: 'error',
      });
      return;
    }
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSaveChanges = async () => {
    setFormErrors({});
    const _formData = new FormData();
    Object.keys(formData).forEach((field) => _formData.append(field, formData[field]));

    if (curAddress.isDirty) {
      const response = await sendRequest({
        method: 'post',
        url: API.USER.updateAddressRequest,
        data: curAddress,
      });

      if (response.success) {
        setCurAddress({ ...curAddress, isDirty: false });
        fetchFaceIdData();
      }
    }

    const response = await sendRequest({
      method: 'put',
      url: API.USER.account,
      params: { userId: user.id },
      data: _formData,
    });
    if (response.success) {
      const { avatar, userToken } = response.data;
      setFormData({ ...formData, avatar });
      setUserAccount(userToken);
      displayNotification({
        message: 'The account is updated successfully.',
        severity: 'success',
      });
    } else {
      setFormErrors(response.data);
    }
  };

  return (
    <Modal
      open
      onClose={closeModal}
      className="flex-row align-c jstfy-c"
      data-testid="change-details-modal"
    >
      <Box
        style={{
          width: 588,
          padding: 32,
          background: '#fff',
          borderRadius: 8,
        }}
      >
        <h3 className="m-b4 text-600" data-testid="modal-title">
          Edit Account
        </h3>
        <Grid container spacing={3} style={{ width: '100%', paddingBottom: '10px', margin: '-14px' }}>
          <Grid item xs={6}>
            <Grid
              container
              columnSpacing={{ xs: 1, sm: 2, md: 3 }}
              style={{ width: '524px', paddingTop: '10px' }}
            >
              <Grid item xs={6}>
                <TextInput
                  name="firstName"
                  label="First Name"
                  value={formData.firstName}
                  onChange={handleFormData}
                  placeholder="Required"
                  error={!!formErrors?.firstName}
                  helperText={formErrors?.firstName}
                />
              </Grid>
              <Grid item xs={6}>
                <TextInput
                  name="lastName"
                  label="Last Name"
                  value={formData.lastName}
                  onChange={handleFormData}
                  placeholder="Required"
                  error={!!formErrors?.lastName}
                  helperText={formErrors?.lastName}
                />
              </Grid>
              <Grid item xs={12}>
                <TextInput
                  name="email"
                  label="Email Address"
                  type="email"
                  value={formData.email}
                  onChange={handleFormData}
                  placeholder="Required"
                  error={!!formErrors?.email}
                  helperText={formErrors?.email}
                  disabled
                />
              </Grid>
              <Grid item xs={12}>
                <div style={{ marginBottom: '16px' }}>
                  <InputLabel
                    sx={{ fontSize: '18px', color: '#212529', margin: 0 }}
                    shrink
                    htmlFor="date-of-birth"
                  >
                    Date of Birth
                  </InputLabel>
                  <div className="custom-date-icon">
                    <DatePicker
                      name="birthday"
                      format="MM/dd/yyyy"
                      id="date-of-birth"
                      oneTap
                      value={new Date(formData.birthday)}
                      onChange={(date) => setFormData({ ...formData, birthday: date })}
                      placeholder="Required"
                      error={formErrors?.birthday ? true : undefined}
                    />
                  </div>
                </div>
              </Grid>
              <Grid item xs={12} sx={{ position: 'relative' }}>
                <PlacesAutocomplete
                  location={curAddress.location}
                  onChange={setCurAddress}
                  textfieldType="outlined"
                  label="Current Address"
                  placeholder="Enter Current Address"
                />
              </Grid>
              {profileUpdateRequests?.address?.new_value?.formattedAddress && (
              <Grid item xs={12}>
                <InputLabel
                  sx={{
                    fontSize: '18px',
                    color: '#212529',
                    margin: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                  shrink
                  htmlFor="new-requested-address"
                >
                  New Requested Address
                  <Typography
                    sx={{
                      fontSize: '12px',
                      color: '#FF5722',
                      marginLeft: '8px',
                      textAlign: 'right',
                    }}
                  >
                    Changes will apply after approval.
                  </Typography>
                </InputLabel>
                <TextInput
                  id="new-requested-address"
                  value={profileUpdateRequests.address.new_value.formattedAddress}
                  disabled
                />
              </Grid>
              )}
              <Grid item xs={12}>
                <InputLabel
                  sx={{ fontSize: '18px', color: '#212529', margin: 0 }}
                  shrink
                  htmlFor="profile-picture"
                >
                  Profile Picture
                </InputLabel>
                <AvatarUploader
                  defaultAvatar={formData.avatar}
                  onChange={(avatar) => setFormData({ ...formData, avatar })}
                  id="profile-picture"
                />
              </Grid>
              <Grid item xs={12} style={{ marginTop: '20px' }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={closeModal}
                  sx={{
                    fontSize: '15px',
                    height: '50px',
                    marginRight: '16px',
                    border: 'inset',
                    borderWidth: 1,
                    borderColor: '#7D8698',
                    padding: '16px 40px',
                    textTransform: 'capitalize',
                    width: '240px',
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveChanges}
                  sx={{
                    fontSize: '15px',
                    height: '50px',
                    padding: '16px 40px',
                    backgroundColor: '#429256',
                    textTransform: 'none',
                    width: '240px',
                  }}
                >
                  Request for Approval
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
}

ChangeDetailsModal.propTypes = {
  formData: PropTypes.shape({
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    birthday: PropTypes.instanceOf(Date).isRequired,
    avatar: PropTypes.string.isRequired,
  }),
  address: PropTypes.shape({
    description: PropTypes.string.isRequired,
    latitude: PropTypes.number.isRequired,
    longitude: PropTypes.number.isRequired,
  }),
  // eslint-disable-next-line react/forbid-prop-types
  profileUpdateRequests: PropTypes.object.isRequired,
  setFormData: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  fetchFaceIdData: PropTypes.func.isRequired,
};

export default ChangeDetailsModal;
